import { useCallback, useMemo } from 'react';

import { DEFAULT_QUICK_PROMPTS, ASK_GREG_DEFAULTS } from '@workspace/constants';
import { useChatContext, waitForChatListenerReady } from '@workspace/widgets/ChatWidget';
import { getBackgroundClass } from '@workspace/lib';
import { AskGregSectionHookResult, AskGregSectionProps } from './ask-greg-section.types';

export function useAskGregSection(props: AskGregSectionProps): AskGregSectionHookResult {
  const {
    title,
    subtitle,
    description,
    quickPromptsTitle,
    quickPrompts,
    isShowButton,
    onPromptClick,
    backgroundColor,
  } = props;

  const { toggleChat, sendMessage, isOpen } = useChatContext();

  const normalizedTitle = title ?? ASK_GREG_DEFAULTS.TITLE;
  const normalizedDescription = description ?? ASK_GREG_DEFAULTS.DESCRIPTION;
  const normalizedQuickPromptsTitle = quickPromptsTitle ?? ASK_GREG_DEFAULTS.QUICK_PROMPTS_TITLE;
  const normalizedQuickPrompts = quickPrompts ?? DEFAULT_QUICK_PROMPTS;
  const normalizedIsShowButton = isShowButton ?? true;

  const backgroundColorClass = useMemo(() => getBackgroundClass(backgroundColor), [backgroundColor]);

  const handleAskGregClick = useCallback(() => {
    toggleChat();
    onPromptClick?.('');
  }, [toggleChat, onPromptClick]);

  const handlePromptClick = useCallback(
    async (promptText: string) => {
      if (!isOpen) {
        toggleChat();
        // Wait for the listener to be ready before sending the message
        await waitForChatListenerReady();
      }
      sendMessage(promptText);
      onPromptClick?.(promptText);
    },
    [toggleChat, sendMessage, isOpen, onPromptClick],
  );

  return {
    title: normalizedTitle,
    subtitle,
    description: normalizedDescription,
    quickPromptsTitle: normalizedQuickPromptsTitle,
    quickPrompts: normalizedQuickPrompts,
    isShowButton: normalizedIsShowButton,
    backgroundColorClass,
    handleAskGregClick,
    handlePromptClick,
  };
}
